"use client"

import { useState, useEffect, useCallback } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { StatsCard } from "@/components/dashboard/stats-card"
import { CustomerStatsCard } from "@/components/dashboard/customer-stats-card"
import { DonutChart } from "@/components/dashboard/donut-chart"
import { ReportTabs } from "@/components/dashboard/report-tabs"
import { Button } from "@/components/ui/button"
import { MoneyIcon } from "@/components/dashboard/sidebar-icons"
import { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent } from "@/components/ui/tab-group"
import Image from "@/src/components/Image"
import { SummaryCategory } from "@/components/dashboard/summary-category"
import { DownloadDatepickerDialog } from "@/components/shared/download-datepicker-dialog"
import type { DateRange } from "react-day-picker"
import { CustomDateRangeDialog } from "@/components/shared/custom-daterange-dialog"
import { useAuth } from "@/src/contexts/AuthContext"
import { usePackageStatus } from "@/hooks/usePackageStatus"
import { KycStatusBanner } from "@/src/components/kyc/KycAlerts"
import { useBusinessData } from "@/src/hooks/useBusinessData"
import Link from "@/src/components/Link"
import { Loader2, RefreshCw } from "lucide-react"
import { getBusinessId } from "@/lib/api/axios-instance"
import transactionsAPI from "@/src/lib/api/transactions"
import { toast } from "sonner"

// Type definitions
interface AccountStatistic {
  count: number
  amount: number
  label: string
}

interface AccountStats {
  account_id: string
  account_number: string | null
  account_name: string
  bank_name: string | null
  statistics: {
    correct: AccountStatistic
    incorrect_receiver: AccountStatistic
    incorrect_amount: AccountStatistic
    incorrect_date: AccountStatistic
    duplicate: AccountStatistic
    expense: AccountStatistic
    not_found: AccountStatistic
    damaged: AccountStatistic
  }
  total_slips: number
  total_amount: number
}

export default function DashboardPage() {
  const { user } = useAuth()
  const { packageStatus } = usePackageStatus()
  const { branches, accounts } = useBusinessData()
  
  // Dashboard stats state
  const [stats, setStats] = useState({
    correctSlips: 0,
    incorrectReceiver: 0,
    incorrectAmount: 0,
    incorrectDate: 0,
    duplicateSlips: 0,
    expenseSlips: 0,
    notFoundSlips: 0,
    damagedSlips: 0,
    totalSlips: 0,
    totalAmount: 0,
  })
  
  const [customerStats, setCustomerStats] = useState({
    totalCustomers: 0,
    newCustomers: 0,
    oldCustomers: 0,
  })
  
  // Account-specific stats state
  const [accountStats, setAccountStats] = useState<AccountStats[]>([])
  const [accountLoading, setAccountLoading] = useState(false)
  const [accountError, setAccountError] = useState<string | null>(null)
  
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isCustomDateRangeOpen, setIsCustomDateRangeOpen] = useState(false)
  const [activeBranchId, setActiveBranchId] = useState("all")
  const [reportPeriod, setReportPeriod] = useState<"today" | "week" | "month" | "custom">("today")
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined)
  
  // Customer section timeframe (separate from main report period)
  const [customerTimeframe, setCustomerTimeframe] = useState<"today" | "week" | "month">("today")
  
  // Add active tab state
  const [activeTab, setActiveTab] = useState("overview")
  const [isRefreshing, setIsRefreshing] = useState(false)

  const buildDateRange = () => {
    const today = new Date()
    const end = new Date(today)
    let start: Date | undefined

    if (reportPeriod === "today") {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    }

    if (reportPeriod === "week") {
      start = new Date(today)
      start.setDate(today.getDate() - 6)
    }

    if (reportPeriod === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1)
    }

    if (reportPeriod === "custom") {
      start = customDateRange?.from
      return {
        dateFrom: start ? start.toISOString().slice(0, 10) : undefined,
        dateTo: customDateRange?.to ? customDateRange.to.toISOString().slice(0, 10) : undefined,
      }
    }

    return {
      dateFrom: start ? start.toISOString().slice(0, 10) : undefined,
      dateTo: end.toISOString().slice(0, 10),
    }
  }

  // Fetch dashboard statistics — extracted as useCallback so it can be called imperatively
  // (e.g., on page-visibility change, manual refresh, etc.)
  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken')
      const businessId = getBusinessId()

      if (!token || !businessId) {
        return
      }

      const { dateFrom, dateTo } = buildDateRange()
      const params = new URLSearchParams()

      if (activeBranchId !== "all") {
        params.append("branch_id", activeBranchId)
      }

      if (dateFrom) {
        params.append("date_from", dateFrom)
      }

      if (dateTo) {
        params.append("date_to", dateTo)
      }

      const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
      const queryString = params.toString()

      // New endpoint: /api/v2/business/:business_id/statistics/dashboard
      const url = queryString
        ? `${baseUrl}/api/v2/business/${businessId}/statistics/dashboard?${queryString}`
        : `${baseUrl}/api/v2/business/${businessId}/statistics/dashboard`

      // Fetch slip statistics
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()

        // Support both new and legacy response formats
        // New format: result.data.statistics
        // Legacy: result.data or result.statistics or result
        const data = result.data?.statistics || result.statistics || result.data || result

        setStats({
          correctSlips: data.correct_slips || data.verified_count || 0,
          incorrectReceiver: data.incorrect_receiver || data.incorrect_receiver_count || 0,
          incorrectAmount: data.incorrect_amount || data.incorrect_amount_count || 0,
          incorrectDate: data.incorrect_date || data.incorrect_date_count || 0,
          duplicateSlips: data.duplicate_slips || data.duplicate_count || 0,
          expenseSlips: data.expense_slips || data.expense_count || 0,
          notFoundSlips: data.not_found_slips || data.not_found_count || 0,
          damagedSlips: data.damaged_slips || data.damaged_count || 0,
          totalSlips: data.total_slips || data.total_count || 0,
          totalAmount: data.total_amount || 0,
        })
      } else {
        console.error('Failed to fetch dashboard statistics:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, reportPeriod, customDateRange])

  // Re-fetch overview stats whenever filters change (or on mount)
  useEffect(() => {
    fetchDashboardStats()
  }, [fetchDashboardStats])

  // Re-fetch per-account breakdown whenever filters or active tab changes
  // This ensures the "แยกตามบัญชี" tab always reflects the current filters
  useEffect(() => {
    if (activeTab === 'individual') {
      fetchByAccountStats()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, reportPeriod, customDateRange, activeTab])

  // Auto-refresh stats when user switches back to this browser tab / window
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardStats()
        if (activeTab === 'individual') {
          fetchByAccountStats()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboardStats, activeTab])

  // Fetch customer statistics
  useEffect(() => {
    const fetchCustomerStats = async () => {
      try {
        const token = localStorage.getItem('authToken')
        const businessId = getBusinessId()
        
        if (!token || !businessId) {
          return
        }

        // Use customerTimeframe from Customer Section tabs
        const timeframe = customerTimeframe
        
        const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
        const url = `${baseUrl}/api/v2/business/${businessId}/statistics/customers?timeframe=${timeframe}`

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          const data = result.data || result
          
          setCustomerStats({
            totalCustomers: data.total_customers || 0,
            newCustomers: data.new_customers || 0,
            oldCustomers: data.old_customers || 0,
          })
        } else {
          console.error('Failed to fetch customer statistics:', response.status, response.statusText)
        }
      } catch (error) {
        console.error('Error fetching customer statistics:', error)
      }
    }

    fetchCustomerStats()
  }, [customerTimeframe])

  // Fetch by-account statistics
  const fetchByAccountStats = async () => {
    if (!user?.customerId) {
      console.error('User ID not available')
      return
    }

    try {
      setAccountLoading(true)
      setAccountError(null) // Clear previous errors
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.error('No auth token found')
        return
      }

      const { dateTo, dateFrom } = buildDateRange()
      const businessId = getBusinessId()
      
      if (!businessId) {
        console.error('Business ID not found')
        return
      }

      // Build query parameters
      const params = new URLSearchParams()
      
      if (activeBranchId && activeBranchId !== 'all') {
        params.append('branch_id', activeBranchId)
      }
      
      if (dateFrom) {
        params.append('date_from', dateFrom)
      }
      
      if (dateTo) {
        params.append('date_to', dateTo)
      }

      const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
      const queryString = params.toString()
      
      // By-account endpoint
      const url = queryString
        ? `${baseUrl}/api/v2/business/${businessId}/statistics/by-account?${queryString}`
        : `${baseUrl}/api/v2/business/${businessId}/statistics/by-account`

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        setAccountStats(result.data || [])
      } else {
        console.error('Failed to fetch account statistics:', response.status, response.statusText)
        
        // Handle specific error cases
        if (response.status === 500) {
          console.error('Database error detected - possibly missing schema columns')
          setAccountError('ระบบฐานข้อมูลมีปัญหา กรุณาติดต่อผู้ดูแลระบบ')
          // Set empty data for graceful degradation
          setAccountStats([])
        } else {
          setAccountError('ไม่สามารถโหลดข้อมูลบัญชีได้ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.error('Error fetching account statistics:', error)
      setAccountError('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setAccountLoading(false)
    }
  }
  
  const handleCustomDateRangeConfirm = (dateRange: DateRange | undefined) => {
    console.log("Selected custom date range:", dateRange)
    setCustomDateRange(dateRange)
    setReportPeriod("custom")
  }
  const handleDownloadConfirm = async (dateRange: DateRange | undefined) => {
    if (!dateRange?.from) {
      toast.error('กรุณาเลือกช่วงวันที่ต้องการดาวน์โหลด')
      return
    }
    setIsDownloading(true)
    try {
      const startDate = dateRange.from.toISOString().slice(0, 10)
      const endDate = (dateRange.to ?? dateRange.from).toISOString().slice(0, 10)
      const blob = await transactionsAPI.exportTransactions('excel', {
        startDate,
        endDate,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transactions_${startDate}_${endDate}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('ดาวน์โหลดเอกสารสำเร็จ')
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'ไม่สามารถดาวน์โหลดเอกสารได้')
    } finally {
      setIsDownloading(false)
    }
  }
  
  // Refresh all dashboard data manually
  const handleRefreshAll = async () => {
    setIsRefreshing(true)
    try {
      await fetchDashboardStats()
      if (activeTab === 'individual') {
        await fetchByAccountStats()
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  // Handle tab change — account stats are re-fetched reactively by useEffect above
  const handleTabChange = (value: string) => {
    setActiveTab(value)
  }
  
  const branchTabs = [
    { id: "all", label: "ทุกสาขา" },
    ...branches.map((branch) => ({
      id: branch.id,
      label: branch.branch_name,
    })),
  ]

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* KYC Status Banner - shows pending/rejected notification */}
        <KycStatusBanner />

        <h1 className="text-2xl font-medium">แดชบอร์ด</h1>

        {/* Store Header */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-lg p-6 mb-4 lg:mb-6 relative shadow-none">
            {/* Edit Icon - Top Right */}
            <Link href="/dashboard/edit-store" className="absolute top-4 right-4">
              <Image src="/icons/icon-store-edit.svg" alt="Edit Store" width={20} height={20} />
            </Link>

            {/* Store Icon and Name - Centered */}
            <div className="flex flex-col items-center mb-6">
              <Image src="/icons/icon-store.svg" alt="Store" width={80} height={80} className="mb-3" />
              <h2 className="text-2xl font-semibold text-gray-900">{user?.shopName || "ร้าน"}</h2>
            </div>

            {/* Credit and Add Credit Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="text-gray-600">
                <span className="text-sm">เครดิต</span>
                <span className="ml-2 font-semibold text-gray-900">{user?.credits?.toLocaleString() || "0"}</span>
              </div>

              <Link href="/user/add-credit" className="no-underline">
                <Button
                  type="button"
                  size="sm"
                  className="bg-brand-red hover:bg-red-600 text-white font-normal py-2 px-4"
                >
                  <MoneyIcon />
                  เติมเครดิต
                </Button>
              </Link>
            </div>

            {/* Branch and Account Stats */}
            <div className="flex items-center justify-between">
              <div className="text-gray-600">
                <span className="text-sm">สาขา</span>
                <span className="ml-2 font-semibold text-gray-900">{branches.length}</span>
              </div>
              <div className="text-gray-600">
                <span className="text-sm">บัญชี</span>
                <span className="ml-2 font-semibold text-gray-900">{accounts.length}</span>
              </div>
            </div>
          </div>

          {/* Package Information Card */}
          <div className="bg-white p-3 mb-4 lg:mb-6 relative shadow-sm text-sm border rounded-2xl pt-4">
            {/* Settings Icon - Top Right */}
            <Link href="/settings/packages" className="absolute top-4 right-4">
              <Image src="/icons/icon-settings.svg" alt="Settings" width={20} height={20} />
            </Link>

            {/* Package Header */}
            <div className="flex items-center justify-between mb-4 pe-8">
              <h3 className="font-medium flex-1">
                <span className="text-gray-700 font-semibold">แพ็คเกจ </span>
                <span className="text-brand-primary font-semibold">
                  {packageStatus?.name || user?.packageName || "ทดลอง"}
                </span>
              </h3>
              <span className="text-sm">คงเหลือ</span>
              <span className="text-brand-primary text-sm">
                {packageStatus?.days_remaining || "0"}วัน
              </span>
            </div>

            {/* Divider */}
            <hr className="mb-4 border-foreground" />

            {/* Package Details */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 text-sm">ราคา</span>
                <span className="text-brand-primary font-medium">
                  {packageStatus?.price ? packageStatus.price.toLocaleString() : "0"}
                </span>
                <span className="text-gray-900 font-medium">บาท</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-700 text-sm">เครดิตต่อเดือน</span>
                <span className="text-brand-primary font-medium">
                  {packageStatus?.included_credits === -1 
                    ? "ไม่จำกัด"
                    : (packageStatus?.included_credits?.toLocaleString() || "0")}
                </span>
                <span className="text-gray-900 font-medium">
                  {packageStatus?.included_credits === -1 ? "" : "เครดิต"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-700 text-sm">ระดับแพ็คเกจ</span>
                <span className="text-brand-primary font-medium">
                  {packageStatus?.level || "—"}
                </span>
                <span className="text-gray-900 font-medium"></span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-700 text-sm">วันที่หมดอายุ</span>
                <span className="text-gray-900 font-medium">
                  {packageStatus?.expires_at 
                    ? new Date(packageStatus.expires_at).toLocaleDateString('th-TH', {
                        day: 'numeric',
                        month: 'short',
                        year: '2-digit'
                      })
                    : "—"
                  }
                </span>
              </div>
            </div>

            {/* Status Footer */}
            <div className="flex items-center justify-between mt-6 pt-4">
              <span className="text-gray-900 font-medium">สถานะ:</span>
              <span className={`font-medium ${packageStatus?.status === 'suspended' ? 'text-brand-red' : 'text-brand-primary'}`}>
                {packageStatus?.status === 'suspended' ? 'ระงับการใช้งาน' :
                 packageStatus?.status === 'active' && !packageStatus?.cancelled_at ? 'กำลังใช้งาน' :
                 packageStatus?.status === 'cancelled_active' ? 'ยกเลิก' :
                 packageStatus?.status === 'expired' ? 'หมดอายุ' :
                 'ไม่ระบุ'}
              </span>
            </div>
          </div>

          {/* Store Menu Card with action buttons */}
          <div className="bg-white p-3 mb-4 lg:mb-6 shadow-sm px-3.5 py-3.5 border rounded-2xl">
            <div className="space-y-2">
              {/* Add Branch */}
              <Link href="/dashboard/branch" className="no-underline">
                <button className="flex items-center space-x-3 w-full hover:bg-gray-50 p-2 rounded-lg transition-colors">
                  <Image src="/icons/icon-add-branch.svg" alt="Add Branch" width={20} height={20} />
                  <span className="text-gray-900 font-medium">เพิ่มสาขา</span>
                </button>
              </Link>

              {/* Add Bank Account */}
              <Link href="/dashboard/accounts" className="no-underline">
                <button className="flex items-center space-x-3 w-full hover:bg-gray-50 p-2 rounded-lg transition-colors">
                  <Image src="/icons/icon-bank.svg" alt="Add Bank" width={20} height={20} />
                  <span className="text-gray-900 font-medium">เพิ่มบัญชีธนาคาร</span>
                </button>
              </Link>

              {/* Connection Settings */}
              <button disabled className="flex items-center space-x-3 w-full p-2 rounded-lg transition-colors opacity-50 cursor-not-allowed">
                <Image src="/icons/icon-settings.svg" alt="Settings" width={20} height={20} />
                <span className="text-gray-900 font-medium">ตั้งค่าการเชื่อมต่อ</span>
              </button>

              {/* Delete Store - Separated with more space and red styling */}
              <button disabled className="flex items-center space-x-3 w-full p-2 rounded-lg transition-colors mt-6 opacity-50 cursor-not-allowed">
                <Image src="/icons/icon-trash.svg" alt="Delete" width={20} height={20} />
                <span className="text-brand-red font-medium">ลบร้านค้า</span>
              </button>
            </div>
          </div>
        </div>

        {/* User Profile Section */}
        

        {/* Reports Section */}
        <div className="grid grid-cols-1 gap-2 mb-4 lg:mb-6">
          {/* Branch Reports */}
          <div className="md:flex items-center justify-center">
            <h3 className="flex-1 text-base lg:text-xl font-semibold text-gray-900 lg:mb-4">
              <span className="font-medium mr-2">รายงาน</span>
              <span className="font-bold">ตามสาขา</span>
            </h3>
            <ReportTabs
              tabs={branchTabs}
              defaultTab="all"
              value={activeBranchId}
              onValueChange={setActiveBranchId}
            />
          </div>

          {/* Time Period Reports */}
          <div className="md:flex items-center justify-center">
            <h3 className="flex-1 text-base lg:text-xl font-semibold text-gray-900 lg:mb-4">
              <span className="font-medium mr-2">รายงาน</span>
              <span className="font-bold">ตามช่วงเวลา</span>
            </h3>
            <TabGroup
              defaultValue="today"
              size="md"
              color="brand-light-red"
              onValueChange={(value) => {
                setReportPeriod(value as "today" | "week" | "month")
                setCustomDateRange(undefined)
              }}
            >
              <TabGroupList>
                <TabGroupTrigger value="today">วันนี้</TabGroupTrigger>
                <TabGroupTrigger value="week">สัปดาห์นี้</TabGroupTrigger>
                <TabGroupTrigger value="month">เดือนนี้</TabGroupTrigger>
                <button
                  type="button"
                  className="px-4 py-2 font-medium text-gray-700 hover:bg-brand-light-red/10 rounded-lg transition-colors"
                  onClick={() => setIsCustomDateRangeOpen(true)}
                >
                  กำหนดเอง
                </button>
              </TabGroupList>
            </TabGroup>
            <Button
              type="button"
              size="sm"
              className="bg-brand-light-red hover:bg-brand-light-red/90 text-white font-normal py-2 px-4 ml-4"
              onClick={() => setIsDatePickerOpen(true)}
              disabled={isDownloading}
            >
              {isDownloading ? 'กำลังดาวน์โหลด...' : 'ดาวน์โหลดเอกสาร'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-gray-600 font-normal py-2 px-3 ml-2"
              onClick={handleRefreshAll}
              disabled={isRefreshing}
              title="รีเฟรชข้อมูล"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <TabGroup 
            value={activeTab}
            defaultValue="overview" 
            size="lg" 
            color="brand-blue" 
            fullWidth
            onValueChange={handleTabChange}
          >
            <TabGroupList>
              <TabGroupTrigger value="overview">ภาพรวม</TabGroupTrigger>
              <TabGroupTrigger value="individual">แยกตามบัญชี</TabGroupTrigger>
            </TabGroupList>

            <TabGroupContent value="overview">
              {/* Summary Section */}
              <div className="bg-white rounded-lg shadow-sm p-3 lg:p-4 mb-4 lg:mb-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-700 mb-6 text-center">ภาพรวม</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                  {/* Left: Donut Chart */}
                  <div className="flex justify-center items-center">
                    <DonutChart
                      size="lg"
                      totalLabel="รวมทั้งหมด"
                      data={[
                        {
                          label: "รายรับ",
                          value: stats.correctSlips + stats.incorrectReceiver,
                          color: "#66bb6a" // brand-primary (green)
                        },
                        {
                          label: "รายจ่าย",
                          value: stats.expenseSlips,
                          color: "#fa2020" // brand-red
                        },
                        {
                          label: "ไม่ตรงเงื่อนไข",
                          value: stats.incorrectDate + stats.incorrectAmount + stats.duplicateSlips,
                          color: "#f97316" // orange-500
                        },
                        {
                          label: "อื่นๆ",
                          value: stats.notFoundSlips + stats.damagedSlips,
                          color: "#a855f7" // purple-500
                        }
                      ]}
                    />
                  </div>

                  {/* Right: Categorized Summary */}
                  <div className="">
                    <div className="p-4 bg-gray-50 rounded-lg mb-3">
                      {/* รายรับ (Income) */}
                      <SummaryCategory
                        title="รายรับ"
                        percentage={stats.totalSlips > 0 ? ((stats.correctSlips + stats.incorrectReceiver) / stats.totalSlips * 100).toFixed(0) + '%' : '0%'}
                        color="primary"
                        items={[
                          { label: "สลิปถูกต้อง", value: `${stats.correctSlips} สลิป` },
                          { label: "บัญชีผู้รับไม่ถูกต้อง", value: `${stats.incorrectReceiver} สลิป` },
                        ]}
                      />

                      {/* รายจ่าย (Expenses) */}
                      <SummaryCategory
                        title="รายจ่าย"
                        percentage={stats.totalSlips > 0 ? (stats.expenseSlips / stats.totalSlips * 100).toFixed(0) + '%' : '0%'}
                        color="red"
                        items={[{ label: "สลิปรายจ่าย", value: `${stats.expenseSlips} สลิป` }]}
                      />

                      {/* ไม่ตรงเงื่อนไข (Does not meet conditions) */}
                      <SummaryCategory
                        title="ไม่ตรงเงื่อนไข"
                        percentage={stats.totalSlips > 0 ? ((stats.incorrectDate + stats.incorrectAmount + stats.duplicateSlips) / stats.totalSlips * 100).toFixed(0) + '%' : '0%'}
                        color="orange"
                        items={[
                          { label: "สลิปวันโอนไม่ถูกต้อง", value: `${stats.incorrectDate} สลิป` },
                          { label: "สลิปยอดเงินไม่ถูกต้อง", value: `${stats.incorrectAmount} สลิป` },
                          { label: "สลิปซ้ำ", value: `${stats.duplicateSlips} สลิป` },
                        ]}
                      />

                      {/* อื่นๆ (Others) */}
                      <SummaryCategory
                        title="อื่นๆ"
                        percentage={stats.totalSlips > 0 ? ((stats.notFoundSlips + stats.damagedSlips) / stats.totalSlips * 100).toFixed(0) + '%' : '0%'}
                        color="purple"
                        items={[
                          { label: "ไม่พบสลิป", value: `${stats.notFoundSlips} สลิป` },
                          { label: "สลิปเสีย", value: `${stats.damagedSlips} สลิป` },
                        ]}
                      />
                    </div>

                    {/* รวมทั้งหมด (Total) */}
                    <div className="p-4 mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-base text-gray-900">รวมทั้งหมด</span>
                        <div className="text-right">
                          <div className="">
                            <span className="text-brand-primary font-semibold me-2">{stats.totalSlips}</span>
                            <span className="text-gray-500 font-semibold">สลิป</span>
                          </div>
                          <div className="">
                            <span className="text-brand-primary font-semibold me-2">
                              {new Intl.NumberFormat('th-TH', {
                                style: 'currency',
                                currency: 'THB',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(stats.totalAmount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ดูสลิปทั้งหมด (View all slips) */}
                    <div className="text-right mt-4">
                      <Link href="/history/checks" className="no-underline">
                        <button className="text-brand-primary hover:text-brand-primary-dark font-medium text-sm">
                          ดูสลิปทั้งหมด
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </TabGroupContent>

            <TabGroupContent value="individual">
              {/* Account Statistics */}
              <div className="rounded-lg mb-4 lg:mb-6">
                <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-3 lg:mb-4">แยกตามบัญชี</h3>
                {accountError ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-red-600 mb-2 font-medium">เกิดข้อผิดพลาด</div>
                    <div className="text-sm text-red-500">{accountError}</div>
                    <button 
                      onClick={() => fetchByAccountStats()} 
                      className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                    >
                      ลองใหม่อีกครั้ง
                    </button>
                  </div>
                ) : accountLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
                  </div>
                ) : accountStats.length > 0 ? (
                  <div className="space-y-6">
                    {accountStats.map((account, index) => (
                      <div key={account.account_id || index} className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                        {/* Account Header */}
                        <div className="mb-4 border-b border-gray-100 pb-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {account.account_name || `บัญชี ${account.account_number}`}
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            {account.account_number && (
                              <span>เลขบัญชี: {account.account_number}</span>
                            )}
                            {account.bank_name && (
                              <span>ธนาคาร: {account.bank_name}</span>
                            )}
                            <span className="font-medium text-gray-900">
                              รวม {account.total_slips} สลิป | {account.total_amount.toLocaleString()} บาท
                            </span>
                          </div>
                        </div>

                        {/* Account Statistics Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          <StatsCard 
                            title="สลิปถูกต้อง" 
                            value={account.statistics.correct.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.correct.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="บัญชีผู้รับไม่ถูกต้อง" 
                            value={account.statistics.incorrect_receiver.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.incorrect_receiver.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="สลิปยอดเงินไม่ถูกต้อง" 
                            value={account.statistics.incorrect_amount.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.incorrect_amount.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="สลิปวันโอนไม่ถูกต้อง" 
                            value={account.statistics.incorrect_date.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.incorrect_date.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="สลิปซ้ำ" 
                            value={account.statistics.duplicate.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.duplicate.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="สลิปรายจ่าย" 
                            value={account.statistics.expense.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.expense.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="ไม่พบสลิป" 
                            value={account.statistics.not_found.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.not_found.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                          <StatsCard 
                            title="สลิปเสีย" 
                            value={account.statistics.damaged.count} 
                            unit="สลิป" 
                            subtitle={`${account.statistics.damaged.amount.toLocaleString()} บาท`} 
                            className="p-3"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                    <div className="text-gray-500 mb-2">ไม่พบข้อมูลบัญชี</div>
                    <div className="text-sm text-gray-400">อาจไม่มีข้อมูลสลิปในช่วงเวลาที่เลือก</div>
                  </div>
                )}
              </div>
            </TabGroupContent>
          </TabGroup>
        </div>

        {/* Customer Section */}
        <div className="rounded-lg p-4 lg:p-6">
          <div className="md:flex items-center justify-center mb-4">
            <h3 className="flex-1 text-base lg:text-xl font-semibold text-gray-900 lg:mb-4">
              <span className="font-medium mr-2">รายงาน</span>
              <span className="font-bold">ลูกค้า</span>
            </h3>
            <TabGroup 
              value={customerTimeframe}
              defaultValue="today" 
              size="md" 
              color="brand-light-red"
              onValueChange={(value) => setCustomerTimeframe(value as "today" | "week" | "month")}
            >
              <TabGroupList>
                <TabGroupTrigger value="today">วันนี้</TabGroupTrigger>
                <TabGroupTrigger value="week">สัปดาห์นี้</TabGroupTrigger>
                <TabGroupTrigger value="month">เดือนนี้</TabGroupTrigger>
              </TabGroupList>
            </TabGroup>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-4 lg:mb-6">
            <CustomerStatsCard title="ลูกค้าทั้งหมด" value={customerStats.totalCustomers} unit="คน" color="green" />
            <CustomerStatsCard title="ลูกค้าใหม่" value={customerStats.newCustomers} unit="คน" color="red" />
            <CustomerStatsCard title="ลูกค้าเก่า" value={customerStats.oldCustomers} unit="คน" color="orange" />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 lg:p-4">
            <h4 className="text-base lg:text-lg font-semibold text-gray-700 mb-6 text-center">ภาพรวม</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Left: Donut Chart */}
              <div className="flex justify-center items-center">
                <DonutChart
                  size="lg"
                  totalLabel="ลูกค้าทั้งหมด"
                  data={[
                    {
                      label: "ลูกค้าใหม่",
                      value: customerStats.newCustomers,
                      color: "#ef4444" // red
                    },
                    {
                      label: "ลูกค้าเก่า",
                      value: customerStats.oldCustomers,
                      color: "#f97316" // orange
                    }
                  ]}
                />
              </div>

              {/* Right: Legend */}
              <div className="flex flex-col justify-center">
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  {/* ลูกค้าใหม่ (New customers) */}
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-brand-red">ลูกค้าใหม่</span>
                    <span className="font-semibold text-lg text-brand-red">
                      {customerStats.totalCustomers > 0 
                        ? ((customerStats.newCustomers / customerStats.totalCustomers) * 100).toFixed(0) 
                        : 0}%
                    </span>
                  </div>

                  {/* ลูกค้าเก่า (Old customers) */}
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-orange-500">ลูกค้าเก่า</span>
                    <span className="font-semibold text-lg text-orange-500">
                      {customerStats.totalCustomers > 0 
                        ? ((customerStats.oldCustomers / customerStats.totalCustomers) * 100).toFixed(0) 
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download Date Picker Dialog */}
      <DownloadDatepickerDialog
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        onConfirm={handleDownloadConfirm}
        loading={isDownloading}
      />

      <CustomDateRangeDialog
        open={isCustomDateRangeOpen}
        onOpenChange={setIsCustomDateRangeOpen}
        onConfirm={handleCustomDateRangeConfirm}
      />
    </DashboardLayout>
  )
}
